import React, { useEffect, useMemo, useState } from 'react';
import { pickBy } from 'lodash';
import Table from 'partial/components/Table';
import SearchBar from 'partial/components/input/SearchBar';
import Pagination from 'partial/components/Pagination';
import ListPanel from 'partial/components/ListPanel';
import Button from 'partial/components/Button';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import { aclHelper } from 'helper';

import { useOpenUpdateMaskName } from './modals/UpdateMaskName';
import { useMaskNameList } from './hooks';

const INITIAL_STATE = {
  keyword: '',
  status: 'pending',
};

export function RequestContent() {
  const { setSubLabel } = useModuleContext();

  const openUpdateMaskName = useOpenUpdateMaskName();
  const [filter, setFilter] = useState(INITIAL_STATE);
  const [page, setPage] = useState({ page: '' });
  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
      }),
    [filter, page],
  );

  useEffect(() => {
    setPage({ page: '' });
  }, [filter]);

  const [isLoading, list, meta] = useMaskNameList(payload);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} mask name request${
            meta?.total === 1 ? '' : 's'
          }`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  const renderAction = (data) => (
    <div className="flex items-center gap-2 justify-end">
      <Button
        danger
        size="sm"
        onClick={() => openUpdateMaskName({ ...data, action: 'reject' })}
      >
        Disapprove
      </Button>
      <Button
        primary
        size="sm"
        onClick={() => openUpdateMaskName({ ...data, action: 'approve' })}
      >
        Approve
      </Button>
    </div>
  );

  const renderLoa = (data) => (
    <a
      href={data.url}
      target="_blank"
      rel="noreferrer noopener"
      className="text-brand-700 font-semibold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 rounded"
    >
      {data.name}
    </a>
  );

  const canUpdate = aclHelper('sender_name', 'u');

  const format = [
    { label: 'MASK', key: 'name' },
    {
      label: 'AGENCY',
      key: 'organization_name',
      contentClassName: 'text-neutral-500',
    },
    {
      label: 'DATE REQUESTED',
      key: 'subscribed_at',
      contentClassName: 'tabular-nums text-neutral-500',
    },
    {
      label: 'DATE APPROVED',
      key: 'approved_at',
      contentClassName: 'tabular-nums text-neutral-500',
    },
    {
      label: 'EXPIRY DATE',
      key: 'expired_at',
      contentClassName: 'tabular-nums text-neutral-500',
    },
    {
      id: 'loa',
      key: (data) => renderLoa(data?.form_upload_url),
      label: 'LOA FORM',
    },
    ...(canUpdate
      ? [
          {
            id: 'action',
            key: (data) => renderAction(data),
            labelClassName: 'whitespace-nowrap',
            contentClassName: 'whitespace-nowrap text-right',
            label: 'ACTION',
          },
        ]
      : []),
  ];

  return (
    <ListPanel
      filters={
        <SearchBar name="keyword" value={filter.keyword} onChange={setFilter} />
      }
      summary={meta?.total ? `${list.length} of ${meta.total} shown` : null}
      pagination={
        +meta?.last_page > 1 ? (
          <Pagination value={meta} onChange={setPage} />
        ) : null
      }
    >
      <Table
        isLoading={isLoading}
        data={list}
        format={format}
        emptySpiel="No mask name requests are awaiting approval."
      />
    </ListPanel>
  );
}

export default RequestContent;
